import { gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';

import { EReactionType } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import {
  usePostLike_LikePost,
  usePostLike_LikePostVariables,
} from './graphql-types/usePostLike_LikePost';
import { usePostLike_PostFragment } from './graphql-types/usePostLike_PostFragment';
import {
  usePostLike_UnlikePost,
  usePostLike_UnlikePostVariables,
} from './graphql-types/usePostLike_UnlikePost';

const POST_FRAGMENT = gql`
  fragment usePostLike_PostFragment on PostV2 {
    _id
    reactionCount
    reactions {
      _id
      user {
        _id
        displayName
      }
    }
    currentUserReaction {
      _id
      reactionType
    }
    tags {
      _id
    }
    announcement {
      _id
    }
    sponsorData {
      audience {
        group {
          _id
        }
      }
    }
    group {
      _id
      isGlobalSingleton
    }
    organization {
      _id
    }
    event {
      _id
    }
  }
`;

const LIKE_POST = gql`
  mutation usePostLike_LikePost($input: CreatePostReactionInput!) {
    createPostReaction(input: $input) {
      data {
        _id
        reactionType
        postV2 {
          _id
          ...usePostLike_PostFragment
        }
      }
    }
  }
  ${POST_FRAGMENT}
`;

const UNLIKE_POST = gql`
  mutation usePostLike_UnlikePost($postId: String!) {
    deletePostReaction(input: { postId: $postId }) {
      data {
        _id
        reactionType
        postV2 {
          _id
          ...usePostLike_PostFragment
        }
      }
    }
  }
  ${POST_FRAGMENT}
`;

export const usePostLike = (
  post: usePostLike_PostFragment
): {
  handleLikeClick: () => Promise<void>;
  isLoading: boolean;
} => {
  const toast = useToast();
  const [likePost, { loading: likePostLoading }] = useMutation<
    usePostLike_LikePost,
    usePostLike_LikePostVariables
  >(LIKE_POST, {
    update(cache) {
      cache.evict({
        id: cache.identify({
          _id: post._id,
          __typename: 'PostV2',
        }),
      });
      cache.gc();
    },
  });

  const [unlikePost, { loading: unlikePostLoading }] = useMutation<
    usePostLike_UnlikePost,
    usePostLike_UnlikePostVariables
  >(UNLIKE_POST, {
    update(cache) {
      cache.evict({
        id: cache.identify({
          _id: post._id,
          __typename: 'PostV2',
        }),
      });
      cache.gc();
    },
  });

  const analyticsClient = useAnalyticsLogger();

  const handleLikeClick = async (): Promise<void> => {
    try {
      if (post.currentUserReaction) {
        await unlikePost({
          variables: {
            postId: post._id,
          },
        });
      } else {
        await likePost({
          variables: {
            input: { postId: post._id, reactionType: EReactionType.LIKE },
          },
        });
        analyticsClient.logEvent_comm_click_like({
          'added tags': !!post?.tags && post.tags.length > 0,
          announcement: !!post.announcement?._id || false,
          'posted in': post.event
            ? 'event'
            : post.organization
            ? 'organization'
            : post.group?.isGlobalSingleton
            ? 'general'
            : 'group',
          'is hosted by sponsor': !!post.sponsorData?.audience.group,
          'post type':
            post.__typename === 'AnswerV2'
              ? 'answer'
              : post.__typename === 'QuestionV2'
              ? 'question'
              : post.__typename === 'BlogV2'
              ? 'article'
              : post.__typename === 'EventAlert'
              ? 'eventPublished'
              : post.__typename === 'PollV2'
              ? 'poll'
              : 'message',
          'on post': true,
          'reaction type': 'like',
        });
      }
    } catch (err) {
      logError(err);
      toast({
        description: 'Failed to update reaction on post',
        status: 'error',
      });
    }
  };

  return {
    handleLikeClick,
    isLoading: likePostLoading || unlikePostLoading,
  };
};

usePostLike.postFragment = POST_FRAGMENT;
